// ✅ ReportBuilderPage.jsx (UI FIXES)
// Fixes requested:
// 1) Available fields grouped by Folder with collapsible sections (Accordion)
// 2) Add-to-columns: Field buttons now have a small picker (Rows / Cols / Values / Filters) instead of always Rows
// 3) Filter typing issue: fixed by using stable keys (FilterId) + no re-mounting on each keystroke
// 4) Save View enabled (UI only): opens dialog to name view + stores locally (localStorage) as a start
//
// NOTE: Backend endpoints unchanged.

import React, { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import DownloadIcon from "@mui/icons-material/Download";
import axios from "axios";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Tabs,
  Tab,
  Button,
  Divider,
  Stack,
  Alert,
  Snackbar,
  Chip,
  IconButton,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { API_URL } from "./config";

export default function ReportBuilderPage() {
  // ✅ Theme constants
  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const BORDER = "rgba(25,118,210,0.20)";
  const BORDER_HOVER = "rgba(25,118,210,0.45)";
  const BORDER_FOCUS = "rgba(240,120,40,0.80)";
  const ROW_ALT = "rgba(25,118,210,0.06)";
  const ROW_HOVER = "rgba(25,118,210,0.10)";

  const pageWrapSx = { p: 2, backgroundColor: BG, minHeight: "100vh" };
  const rootPaperSx = {
    mx: "auto",
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
  };
  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
    flexWrap: "wrap",
  };
  const bodySx = { p: 2, backgroundColor: BG };

  const surfaceSx = {
    borderRadius: 2,
    backgroundColor: "white",
    border: `1px solid ${BORDER}`,
  };

  const hScrollSx = {
  width: "100%",
  maxWidth: "100%",
  overflowX: "auto",
  overflowY: "hidden",
  WebkitOverflowScrolling: "touch",
  };

  const previewCardSx = {
  ...surfaceSx,
  p: 2,
  // IMPORTANT: allow inner scrollers to be visible
  overflow: "visible",
  minWidth: 0, // critical for grid children so overflow can work
};

const tableMaxContentSx = (colCount) => ({
  width: "max-content",       // ✅ key: table expands
  minWidth: "100%",           // but never smaller than container
  tableLayout: "auto",
  // optional: ensure it becomes wide enough to require scroll
  ...(colCount ? { minWidth: Math.max(900, colCount * 160) } : {}),
});

  const inputSx = {
    "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_HOVER },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_FOCUS },
    "& .MuiInputLabel-root.Mui-focused": { color: THEME_ORANGE },
  };

  const previewTableWrapSx = {
    borderRadius: 2,
    overflow: "hidden",                 // ✅ important for rounded corners
    border: "1px solid rgba(25,118,210,0.15)",
    backgroundColor: "#fff",
  };

 const previewTableSx = (colCount) => ({
   width: "max-content",
   minWidth: "100%",
   ...(colCount ? { minWidth: Math.max(900, colCount * 160) } : {}),
   tableLayout: "auto",
   "& thead th:first-of-type": { borderTopLeftRadius: 12 },
   "& thead th:last-of-type": { borderTopRightRadius: 12 },
 });

  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: THEME_ORANGE,
    "&:hover": { backgroundColor: THEME_ORANGE_2 },
  };

  const outlinedBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    borderColor: "rgba(25,118,210,0.35)",
    color: "rgba(25,118,210,0.95)",
    "&:hover": {
      borderColor: "rgba(25,118,210,0.55)",
      backgroundColor: "rgba(25,118,210,0.04)",
    },
  };

  const subtleBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    borderColor: "rgba(25,118,210,0.22)",
    color: "rgba(25,118,210,0.95)",
    backgroundColor: "rgba(25,118,210,0.02)",
    "&:hover": {
      borderColor: "rgba(25,118,210,0.45)",
      backgroundColor: "rgba(25,118,210,0.06)",
    },
  };

  const iconBtnSx = {
    borderRadius: 2,
    color: "rgba(25,118,210,0.95)",
    "&:hover": { backgroundColor: "rgba(240,120,40,0.10)", color: THEME_ORANGE },
  };

  const theadRowSx = {
    backgroundColor: "rgba(25,118,210,0.95)",
    "& th": {
      color: "#fff",
      fontWeight: 900,
      borderBottom: "1px solid rgba(255,255,255,0.18)",
      whiteSpace: "nowrap",
    },
  };

  const zebraRowSx = (idx) => ({
    backgroundColor: idx % 2 === 0 ? ROW_ALT : "#FFFFFF",
    "&:hover": { backgroundColor: ROW_HOVER },
    "& td": { borderBottom: "1px solid rgba(25,118,210,0.10)" },
  });

  // ================== AUTH (MATCHES ApproverTimeOffRequest) ==================
  const token = localStorage.getItem("auth");
  const [loginCampusCode, setLoginCampusCode] = useState("");
  const [toast, setToast] = useState({ open: false, type: "success", msg: "" });

  useEffect(() => {
    if (!token) return;
    try {
      const decodedToken = JSON.parse(atob(token.split(".")[1]));
      setLoginCampusCode(decodedToken.campusCode || decodedToken.CampusCode || "");
    } catch (e) {
      console.error("Error decoding token:", e);
      setLoginCampusCode("");
      setToast({ open: true, type: "error", msg: "Invalid session token." });
    }
  }, [token]);

  const authHeader = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  // ================== STATE ==================
  const [models, setModels] = useState([]);
  const [modelCode, setModelCode] = useState("STUDENT_MARKS");
  const [folders, setFolders] = useState({});
  const [search, setSearch] = useState("");

  const [rows, setRows] = useState(["em.Semester"]);
  const [cols, setCols] = useState(["csd.SubjectNameCode"]);
  const [values, setValues] = useState([{ field: "cg.Grade" }]);
  // ✅ filter rows with stable ids
  const makeFilter = () => ({
    FilterId: `F_${Date.now()}_${Math.random().toString(16).slice(2)}`,
    field: "cg.ResultStatus",
    op: "=",
    value: "Fail",
  });
  const [filters, setFilters] = useState([makeFilter()]);

  const [data, setData] = useState([]);
  const [tab, setTab] = useState(0);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  // ✅ field add target
  const [addTarget, setAddTarget] = useState("ROWS"); // ROWS | COLS | VALUES | FILTERS

  // ✅ collapsible folders state
  const [expandedFolders, setExpandedFolders] = useState(() => new Set());

  // ✅ Save View dialog (UI only)
  const [saveOpen, setSaveOpen] = useState(false);
  const [viewName, setViewName] = useState("");
  const [savingView, setSavingView] = useState(false);


  // ================== AI ANALYTICS ==================
  const [aiLoading, setAiLoading] = useState(false);
  const [aiOutputType, setAiOutputType] = useState("ALL");
  const [aiResult, setAiResult] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiChartPrompt, setAiChartPrompt] = useState("");

  // ================== HELPERS ==================
  const allFieldsFlat = useMemo(() => {
    const out = [];
    Object.keys(folders || {}).forEach((folderName) => {
      (folders[folderName] || []).forEach((f) => out.push({ ...f, Folder: folderName }));
    });
    return out;
  }, [folders]);

  const fieldByCode = useMemo(() => {
    const m = new Map();
    allFieldsFlat.forEach((f) => m.set(f.FieldCode, f));
    return m;
  }, [allFieldsFlat]);

  const matchesSearch = (text) => {
    const s = String(search || "").trim().toLowerCase();
    if (!s) return true;
    return String(text || "").toLowerCase().includes(s);
  };

  const safeLabel = (fieldCode) => fieldByCode.get(fieldCode)?.FieldLabel || fieldCode;

  const addUnique = (arr, fieldCode) => (arr.includes(fieldCode) ? arr : [...arr, fieldCode]);
  const removeOne = (arr, fieldCode) => arr.filter((x) => x !== fieldCode);

  const addValueUnique = (arr, fieldCode) =>
    arr.some((v) => v.field === fieldCode) ? arr : [...arr, { field: fieldCode }];

  const removeValue = (arr, fieldCode) => arr.filter((v) => v.field !== fieldCode);

  // ✅ add-field action (fixes #2)
  const handleAddField = (fieldCode) => {
    const role = String(fieldByCode.get(fieldCode)?.Role || "").toUpperCase();

    // If user explicitly chooses a target, honor it.
    // But if they choose VALUES and field isn't MEASURE/DISPLAY, still allow (some models may not set Role reliably).
    if (addTarget === "ROWS") return setRows((r) => addUnique(r, fieldCode));
    if (addTarget === "COLS") return setCols((c) => addUnique(c, fieldCode));
    if (addTarget === "VALUES") return setValues((v) => addValueUnique(v, fieldCode));
    if (addTarget === "FILTERS") {
      return setFilters((arr) => [
        ...arr,
        { FilterId: `F_${Date.now()}_${Math.random().toString(16).slice(2)}`, field: fieldCode, op: "=", value: "" },
      ]);
    }

    // fallback
    if (role === "MEASURE" || role === "DISPLAY") setValues((v) => addValueUnique(v, fieldCode));
    else setRows((r) => addUnique(r, fieldCode));
  };

  // Excel-like pivot builder.
  // - Rows may contain one or more fields.
  // - Columns are OPTIONAL.
  // - Values are OPTIONAL (Rows-only pivot returns distinct row combinations).
  // - Duplicate source records collapse into one pivot row/bucket.
  function pivotData(dataRows, rowFields = [], colFields = [], valueFields = []) {
    const rFields = (rowFields || []).filter(Boolean);
    const cFields = (colFields || []).filter(Boolean);
    const vFields = (valueFields || []).map((v) => v?.field).filter(Boolean);
    const source = Array.isArray(dataRows) ? dataRows : [];

    const keyOf = (record, fields) =>
      JSON.stringify(fields.map((field) => record?.[field] ?? ""));

    const labelOf = (record, fields) =>
      fields.map((field) => String(record?.[field] ?? "")).join(" | ");

    // No column fields: behave like an Excel pivot with row labels only.
    // This is the important case for StudentID + BacklogCount: StudentID appears once.
    if (cFields.length === 0) {
      const grouped = new Map();

      for (const record of source) {
        const rowKey = keyOf(record, rFields);
        if (!grouped.has(rowKey)) {
          const out = {};
          rFields.forEach((field) => {
            out[field] = record?.[field] ?? "";
          });
          vFields.forEach((field) => {
            out[field] = record?.[field] ?? "";
          });
          grouped.set(rowKey, out);
          continue;
        }

        // Keep the first meaningful value for a bucket. This prevents a student-level
        // value such as BacklogCount from being repeated/multiplied by subject joins.
        const out = grouped.get(rowKey);
        vFields.forEach((field) => {
          const current = out[field];
          const incoming = record?.[field];
          if ((current === "" || current == null) && incoming !== "" && incoming != null) {
            out[field] = incoming;
          }
        });
      }

      // If there are no Row fields, collapse identical selected values to distinct rows.
      if (rFields.length === 0) {
        const distinct = new Map();
        for (const record of source) {
          const out = {};
          vFields.forEach((field) => {
            out[field] = record?.[field] ?? "";
          });
          const k = JSON.stringify(vFields.map((field) => out[field]));
          if (!distinct.has(k)) distinct.set(k, out);
        }
        return { cols: vFields, rows: Array.from(distinct.values()) };
      }

      return { cols: [...rFields, ...vFields], rows: Array.from(grouped.values()) };
    }

    // Column pivot: one result row per distinct Rows combination and one output
    // column per distinct Columns combination (and per Value when multiple Values exist).
    const columnKeys = [];
    const columnKeySet = new Set();
    const grouped = new Map();

    for (const record of source) {
      const rowKey = keyOf(record, rFields);
      const columnKey = keyOf(record, cFields);
      const columnLabel = labelOf(record, cFields);

      if (!columnKeySet.has(columnKey)) {
        columnKeySet.add(columnKey);
        columnKeys.push({ key: columnKey, label: columnLabel });
      }

      if (!grouped.has(rowKey)) {
        const out = {};
        rFields.forEach((field) => {
          out[field] = record?.[field] ?? "";
        });
        grouped.set(rowKey, out);
      }

      const out = grouped.get(rowKey);

      if (vFields.length === 0) {
        // Rows + Columns without Values is valid in Excel. Show membership only.
        out[columnLabel] = "";
      } else {
        vFields.forEach((valueField) => {
          const header =
            vFields.length === 1
              ? columnLabel
              : `${columnLabel} • ${safeLabel(valueField)}`;
          const incoming = record?.[valueField];
          if (!(header in out) || out[header] === "" || out[header] == null) {
            out[header] = incoming ?? "";
          }
        });
      }
    }

    const dynamicCols = [];
    for (const c of columnKeys) {
      if (vFields.length <= 1) dynamicCols.push(c.label);
      else vFields.forEach((field) => dynamicCols.push(`${c.label} • ${safeLabel(field)}`));
    }

    return { cols: [...rFields, ...dynamicCols], rows: Array.from(grouped.values()) };
  }

  function downloadRowsAsXlsx(rowsArr, filenameBase) {
  const safeRows = Array.isArray(rowsArr) ? rowsArr : [];
  const ws = XLSX.utils.json_to_sheet(safeRows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Report");

  const out = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  const blob = new Blob([out], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  const ts = new Date();
  const stamp = `${ts.getFullYear()}-${String(ts.getMonth() + 1).padStart(2, "0")}-${String(
    ts.getDate()
  ).padStart(2, "0")}_${String(ts.getHours()).padStart(2, "0")}${String(ts.getMinutes()).padStart(
    2,
    "0"
  )}`;

  saveAs(blob, `${filenameBase}_${stamp}.xlsx`);
  }

  const downloadFlat = () => {
  if (!data?.length) {
    setToast({ open: true, type: "error", msg: "Nothing to download. Run the report first." });
    return;
  }
  downloadRowsAsXlsx(data, `Report_${modelCode}_FLAT`);
  };

  const downloadPivot = () => {
    if (!data?.length) {
      setToast({ open: true, type: "error", msg: "Nothing to download. Run the report first." });
      return;
    }
    if ((rows || []).length === 0 && (cols || []).length === 0 && (values || []).length === 0) {
      setToast({ open: true, type: "error", msg: "Add at least one field to the Pivot designer." });
      return;
    }
    const p = pivotData(data || [], rows, cols, values);
    downloadRowsAsXlsx(p.rows, `Report_${modelCode}_PIVOT`);
  };

  // ================== API ==================
  const fetchModels = async () => {
    const res = await axios.get(`${API_URL}/api/reportbuilder/models`, {
      ...authHeader,
      params: { CampusCode: loginCampusCode },
    });
    setModels(res.data || []);
  };

  const fetchFields = async (mc) => {
    const res = await axios.get(`${API_URL}/api/reportbuilder/models/${mc}/fields`, {
      ...authHeader,
      params: { CampusCode: loginCampusCode },
    });
    setFolders(res.data?.folders || {});
  };

  useEffect(() => {
    if (!token) {
      setError("Missing auth token. Please login again.");
      return;
    }
    if (!loginCampusCode) return;
    fetchModels().catch((e) =>
      setError(e?.response?.data?.error || e.message || "Failed to load report models")
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, loginCampusCode]);

  useEffect(() => {
    if (!token || !loginCampusCode) return;
    fetchFields(modelCode).catch((e) =>
      setError(e?.response?.data?.error || e.message || "Failed to load fields")
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelCode, token, loginCampusCode]);

  const runReport = async () => {
    setLoading(true);
    setError("");
    try {
      const payload = {
        CampusCode: loginCampusCode,
        modelCode,
        rows,
        cols,
        values,
        mode: tab === 1 ? "PIVOT" : "FLAT",
        // backend likely expects plain array; keep keys but harmless if ignored:
        filters: (filters || []).map(({ FilterId, ...rest }) => rest),
        limit: 100000,
      };

      const r = await axios.post(`${API_URL}/api/reportbuilder/run`, payload, {
        ...authHeader,
        params: { CampusCode: loginCampusCode },
      });

      setData(r.data?.rows || []);
      setSuccess(true);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || "Failed to run report");
    } finally {
      setLoading(false);
    }
  };

const generateAiAnalytics = async (requestedType = aiOutputType) => {
    setAiLoading(true);
    setError("");
    try {
      const payload = {
        CampusCode: loginCampusCode,
        modelCode,
        viewName: selectedViewName || null,
        rows,
        cols,
        values,
        filters: (filters || []).map(({ FilterId, ...rest }) => rest),
        mode: tab === 1 ? "PIVOT" : "FLAT",
        outputType: requestedType,
        chartPrompt: requestedType === "CHARTS" ? aiChartPrompt.trim() : "",
      };

      const resp = await axios.post(
        `${API_URL}/api/reportbuilder/ai/generate`,
        payload,
        {
          ...authHeader,
          params: { CampusCode: loginCampusCode },
        }
      );

      setAiResult(resp.data || null);
      setAiOutputType(requestedType);
      setAiOpen(true);
    } catch (e) {
      setError(
        e?.response?.data?.error ||
          e.message ||
          "Failed to generate AI analytics"
      );
    } finally {
      setAiLoading(false);
    }
  };

  const downloadTextFile = (content, filename, mime = "text/plain;charset=utf-8") => {
    const blob = new Blob([content], { type: mime });
    saveAs(blob, filename);
  };

  const downloadAiReport = () => {
    const a = aiResult?.analytics || {};
    const r = a?.detailedReport || {};
    const lines = [
      a?.title || "AI Analytics Report",
      "",
      "EXECUTIVE SUMMARY",
      a?.executiveSummary || "",
      "",
      "OVERVIEW",
      r?.overview || "",
      "",
      "PERFORMANCE ANALYSIS",
      ...(r?.performanceAnalysis || []).map((x) => `• ${x}`),
      "",
      "DISTRIBUTION ANALYSIS",
      ...(r?.distributionAnalysis || []).map((x) => `• ${x}`),
      "",
      "RISKS AND EXCEPTIONS",
      ...(r?.risksAndExceptions || []).map((x) => `• ${x}`),
      "",
      "POSITIVE INDICATORS",
      ...(r?.positiveIndicators || []).map((x) => `• ${x}`),
      "",
      "RECOMMENDATIONS",
      ...(r?.recommendations || []).map((x) => `• ${x}`),
      "",
      "METHODOLOGY",
      r?.methodologyNote || "",
      "",
      "PRIVACY",
      r?.privacyNote || aiResult?.privacy?.note || "",
    ];
    downloadTextFile(
      lines.join("\n"),
      `AI_Report_${modelCode}_${new Date().toISOString().slice(0, 10)}.txt`
    );
  };

  const downloadPresentationHtml = () => {
    const deck = aiResult?.analytics?.presentation;
    if (!deck?.slides?.length) return;

    const esc = (v) =>
      String(v ?? "")
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;")
        .replaceAll('"', "&quot;");

    const slides = deck.slides
      .map(
        (s) => `
<section class="slide">
  <div class="slide-no">${esc(s.slideNumber || "")}</div>
  <h1>${esc(s.title || "")}</h1>
  <ul>${(s.bullets || []).map((b) => `<li>${esc(b)}</li>`).join("")}</ul>
  ${s.chartId ? `<div class="chart-ref">Chart: ${esc(s.chartId)}</div>` : ""}
  ${s.speakerNotes ? `<div class="notes"><strong>Speaker notes:</strong> ${esc(s.speakerNotes)}</div>` : ""}
</section>`
      )
      .join("\n");

    const html = `<!doctype html>
<html>
<head>
<meta charset="utf-8"/>
<title>${esc(deck.deckTitle || "UniFLOO Analytics Presentation")}</title>
<style>
@page { size: 13.333in 7.5in; margin: 0; }
* { box-sizing: border-box; }
body { margin: 0; background: #eee; font-family: Arial, sans-serif; }
.slide { width: 13.333in; height: 7.5in; padding: .65in .8in; background: white; page-break-after: always; position: relative; }
.slide h1 { color: #1976d2; font-size: 30pt; margin: 0 0 .35in; }
.slide li { font-size: 18pt; margin: .15in 0; line-height: 1.35; }
.slide-no { position: absolute; right: .35in; bottom: .25in; color: #888; }
.chart-ref { margin-top: .25in; padding: .15in; border-left: 5px solid #F07828; font-size: 14pt; }
.notes { position: absolute; left: .8in; right: .8in; bottom: .28in; font-size: 9pt; color: #666; }
@media print { body { background: white; } .slide { break-after: page; } }
</style>
</head>
<body>${slides}</body></html>`;

    downloadTextFile(
      html,
      `AI_Presentation_${modelCode}_${new Date().toISOString().slice(0, 10)}.html`,
      "text/html;charset=utf-8"
    );
  };

  const SimpleAiChart = ({ chart }) => {
    const chartType = String(chart?.chartType || "bar").toLowerCase();
    const data = Array.isArray(chart?.data) ? chart.data.slice(0, 12) : [];
    const xKey = chart?.xKey || "category";
    const series = Array.isArray(chart?.series) ? chart.series.filter((x) => x?.dataKey) : [];
    if (!data.length || !series.length) return null;

    const palette = [THEME_ORANGE, "#1976d2", "#2e7d32", "#7b1fa2", "#d32f2f", "#0288d1", "#6d4c41", "#455a64"];
    const W = 620;
    const H = 280;
    const PAD = { l: 58, r: 22, t: 18, b: 62 };
    const innerW = W - PAD.l - PAD.r;
    const innerH = H - PAD.t - PAD.b;
    const allVals = data.flatMap((d) => series.map((sr) => Number(d?.[sr.dataKey])).filter(Number.isFinite));
    const maxY = Math.max(...allVals, 1);
    const minY = chartType === "scatter" ? Math.min(...allVals, 0) : 0;
    const yScale = (v) => PAD.t + innerH - ((Number(v) - minY) / Math.max(maxY - minY, 1)) * innerH;

    const renderBar = () => {
      const groupW = innerW / Math.max(data.length, 1);
      const barW = Math.max(3, Math.min(24, (groupW * 0.72) / Math.max(series.length, 1)));
      return (
        <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label={chart?.title || "AI chart"}>
          <line x1={PAD.l} y1={PAD.t + innerH} x2={W - PAD.r} y2={PAD.t + innerH} stroke="rgba(0,0,0,0.25)" />
          <line x1={PAD.l} y1={PAD.t} x2={PAD.l} y2={PAD.t + innerH} stroke="rgba(0,0,0,0.25)" />
          {data.map((d, i) => {
            const gx = PAD.l + i * groupW + groupW * 0.14;
            return (
              <g key={`${String(d?.[xKey])}_${i}`}>
                {series.map((sr, si) => {
                  const val = Number(d?.[sr.dataKey]);
                  if (!Number.isFinite(val)) return null;
                  const y = yScale(val);
                  return <rect key={sr.dataKey} x={gx + si * barW} y={y} width={barW - 1} height={PAD.t + innerH - y} rx="2" fill={palette[si % palette.length]} />;
                })}
                <text x={PAD.l + i * groupW + groupW / 2} y={H - 39} textAnchor="middle" fontSize="9" transform={`rotate(-28 ${PAD.l + i * groupW + groupW / 2} ${H - 39})`}>
                  {String(d?.[xKey] ?? "").slice(0, 18)}
                </text>
              </g>
            );
          })}
        </svg>
      );
    };

    const renderLine = () => (
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label={chart?.title || "AI chart"}>
        <line x1={PAD.l} y1={PAD.t + innerH} x2={W - PAD.r} y2={PAD.t + innerH} stroke="rgba(0,0,0,0.25)" />
        <line x1={PAD.l} y1={PAD.t} x2={PAD.l} y2={PAD.t + innerH} stroke="rgba(0,0,0,0.25)" />
        {series.map((sr, si) => {
          const pts = data.map((d, i) => {
            const x = PAD.l + (data.length === 1 ? innerW / 2 : (i / (data.length - 1)) * innerW);
            const val = Number(d?.[sr.dataKey]);
            return Number.isFinite(val) ? { x, y: yScale(val), val } : null;
          }).filter(Boolean);
          return (
            <g key={sr.dataKey}>
              {pts.length > 1 && <polyline fill="none" stroke={palette[si % palette.length]} strokeWidth="3" points={pts.map((p) => `${p.x},${p.y}`).join(" ")} />}
              {pts.map((p, i) => <circle key={i} cx={p.x} cy={p.y} r="4" fill={palette[si % palette.length]} />)}
            </g>
          );
        })}
        {data.map((d, i) => {
          const x = PAD.l + (data.length === 1 ? innerW / 2 : (i / (data.length - 1)) * innerW);
          return <text key={i} x={x} y={H - 28} textAnchor="middle" fontSize="9" transform={`rotate(-25 ${x} ${H - 28})`}>{String(d?.[xKey] ?? "").slice(0, 16)}</text>;
        })}
      </svg>
    );

    const renderScatter = () => {
      const yKey = series[0].dataKey;
      const xs = data.map((d) => Number(d?.[xKey])).filter(Number.isFinite);
      const ys = data.map((d) => Number(d?.[yKey])).filter(Number.isFinite);
      const minX = Math.min(...xs, 0);
      const maxX = Math.max(...xs, 1);
      const minScatterY = Math.min(...ys, 0);
      const maxScatterY = Math.max(...ys, 1);
      const sx = (v) => PAD.l + ((v - minX) / Math.max(maxX - minX, 1)) * innerW;
      const sy = (v) => PAD.t + innerH - ((v - minScatterY) / Math.max(maxScatterY - minScatterY, 1)) * innerH;
      return (
        <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label={chart?.title || "AI chart"}>
          <line x1={PAD.l} y1={PAD.t + innerH} x2={W - PAD.r} y2={PAD.t + innerH} stroke="rgba(0,0,0,0.25)" />
          <line x1={PAD.l} y1={PAD.t} x2={PAD.l} y2={PAD.t + innerH} stroke="rgba(0,0,0,0.25)" />
          {data.map((d, i) => {
            const x = Number(d?.[xKey]);
            const y = Number(d?.[yKey]);
            if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
            return <circle key={i} cx={sx(x)} cy={sy(y)} r="6" fill={THEME_ORANGE} opacity="0.82" />;
          })}
          <text x={PAD.l + innerW / 2} y={H - 8} textAnchor="middle" fontSize="11">{chart?.xAxisLabel || xKey}</text>
        </svg>
      );
    };

    const renderPie = () => {
      const yKey = series[0].dataKey;
      const vals = data.map((d) => Math.max(0, Number(d?.[yKey]) || 0));
      const total = vals.reduce((a, b) => a + b, 0) || 1;
      let angle = -Math.PI / 2;
      const cx = 170, cy = 135, r = 92;
      const arcs = vals.map((v, i) => {
        const start = angle;
        const delta = (v / total) * Math.PI * 2;
        const end = angle + delta;
        angle = end;
        const x1 = cx + r * Math.cos(start), y1 = cy + r * Math.sin(start);
        const x2 = cx + r * Math.cos(end), y2 = cy + r * Math.sin(end);
        const large = delta > Math.PI ? 1 : 0;
        return { path: `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z`, value: v, i };
      });
      return (
        <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label={chart?.title || "AI chart"}>
          {arcs.map((a) => <path key={a.i} d={a.path} fill={palette[a.i % palette.length]} />)}
          {data.slice(0, 8).map((d, i) => (
            <g key={i}>
              <rect x="315" y={35 + i * 27} width="12" height="12" rx="2" fill={palette[i % palette.length]} />
              <text x="334" y={45 + i * 27} fontSize="11">{String(d?.[xKey] ?? "").slice(0, 24)} ({Math.round((vals[i] / total) * 100)}%)</text>
            </g>
          ))}
        </svg>
      );
    };

    const visual = chartType === "line" ? renderLine() : chartType === "scatter" ? renderScatter() : chartType === "pie" ? renderPie() : renderBar();

    return (
      <Paper elevation={0} sx={{ ...surfaceSx, p: 1.5, minWidth: 0 }}>
        <Typography sx={{ fontWeight: 900, color: "rgba(25,118,210,0.95)" }}>
          {chart?.title || "AI Chart"}
        </Typography>
        {!!chart?.description && <Typography sx={{ fontSize: "0.78rem", opacity: 0.72, mt: 0.25 }}>{chart.description}</Typography>}
        {!!chart?.question && <Typography sx={{ fontSize: "0.78rem", mt: 0.6 }}><strong>Question:</strong> {chart.question}</Typography>}
        <Box sx={{ width: "100%", overflowX: "auto", mt: 1 }}>{visual}</Box>
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 0.5 }}>
          {series.map((sr, i) => (
            <Chip key={sr.dataKey} size="small" label={sr.label || sr.dataKey} sx={{ "&:before": { content: '""', width: 8, height: 8, borderRadius: 99, backgroundColor: palette[i % palette.length], mr: 0.7 } }} />
          ))}
        </Stack>
        {!!chart?.insight && (
          <Alert severity="info" sx={{ mt: 1.2, py: 0.2 }}>
            <strong>What it shows:</strong> {chart.insight}
          </Alert>
        )}
        {!!chart?.rationale && <Typography sx={{ fontSize: "0.75rem", opacity: 0.72, mt: 0.8 }}><strong>Why this chart:</strong> {chart.rationale}</Typography>}
        {!!chart?.privacyNote && <Typography sx={{ fontSize: "0.7rem", opacity: 0.58, mt: 0.4 }}>{chart.privacyNote}</Typography>}
      </Paper>
    );
  };

  const AiAnalyticsPanel = () => {
    if (!aiResult) return null;
    const a = aiResult?.analytics || {};
    const charts = aiResult?.deterministic?.charts || [];
    const deck = a?.presentation || {};

    return (
      <Dialog
        open={aiOpen}
        onClose={() => setAiOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 900, color: "rgba(25,118,210,0.95)" }}>
          {a?.title || "AI Analytics Studio"}
        </DialogTitle>
        <DialogContent dividers sx={{ backgroundColor: BG }}>
          <Stack spacing={2}>
            <Paper elevation={0} sx={{ ...surfaceSx, p: 2 }}>
              <Typography sx={{ fontWeight: 900 }}>Executive Summary</Typography>
              <Typography sx={{ mt: 0.5, whiteSpace: "pre-line" }}>
                {a?.executiveSummary || "No executive summary returned."}
              </Typography>
              {!!aiResult?.aiMeta && (
                <Typography sx={{ mt: 1, fontSize: "0.75rem", opacity: 0.65 }}>
                  AI: {aiResult.aiMeta.provider || "provider"} • {aiResult.aiMeta.model || "model"}
                  {" • "}
                  Privacy: raw rows sent to AI = {String(aiResult?.privacy?.rawRowsSentToAI)}
                </Typography>
              )}
            </Paper>

            {!!a?.kpis?.length && (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(2,1fr)", md: "repeat(4,1fr)" },
                  gap: 1,
                }}
              >
                {a.kpis.map((k, idx) => (
                  <Paper key={idx} elevation={0} sx={{ ...surfaceSx, p: 1.5 }}>
                    <Typography sx={{ fontSize: "0.75rem", opacity: 0.7 }}>{k.label}</Typography>
                    <Typography sx={{ fontSize: "1.35rem", fontWeight: 900, color: THEME_ORANGE }}>
                      {k.value}
                    </Typography>
                    <Typography sx={{ fontSize: "0.75rem", opacity: 0.75 }}>{k.context}</Typography>
                  </Paper>
                ))}
              </Box>
            )}

            {!!a?.insights?.length && (
              <Paper elevation={0} sx={{ ...surfaceSx, p: 2 }}>
                <Typography sx={{ fontWeight: 900, mb: 1 }}>AI Insights</Typography>
                <Stack spacing={1}>
                  {a.insights.map((x, idx) => (
                    <Box key={idx} sx={{ borderLeft: `4px solid ${THEME_ORANGE}`, pl: 1.25 }}>
                      <Typography sx={{ fontWeight: 900 }}>
                        {x.priority ? `${x.priority} • ` : ""}{x.title}
                      </Typography>
                      <Typography sx={{ fontSize: "0.88rem" }}>{x.finding}</Typography>
                      {!!x.evidence && (
                        <Typography sx={{ fontSize: "0.78rem", opacity: 0.75 }}>
                          Evidence: {x.evidence}
                        </Typography>
                      )}
                      {!!x.recommendedAction && (
                        <Typography sx={{ fontSize: "0.82rem", mt: 0.4, fontWeight: 700 }}>
                          Action: {x.recommendedAction}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Stack>
              </Paper>
            )}

            {!!charts.length && (
              <Box>
                <Typography sx={{ fontWeight: 900, mb: 1, color: "rgba(25,118,210,0.95)" }}>
                  {aiResult?.outputType === "CHARTS" ? "AI-Designed Charts" : "Deterministic Charts"}
                </Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1.5 }}>
                  {charts.map((chart) => (
                    <SimpleAiChart key={chart.id} chart={chart} />
                  ))}
                </Box>
              </Box>
            )}

            {!!a?.detailedReport && (
              <Paper elevation={0} sx={{ ...surfaceSx, p: 2 }}>
                <Typography sx={{ fontWeight: 900, mb: 1 }}>Detailed Report</Typography>
                <Typography sx={{ fontWeight: 800 }}>Overview</Typography>
                <Typography sx={{ mb: 1 }}>{a.detailedReport.overview}</Typography>

                {[
                  ["Performance Analysis", a.detailedReport.performanceAnalysis],
                  ["Distribution Analysis", a.detailedReport.distributionAnalysis],
                  ["Risks & Exceptions", a.detailedReport.risksAndExceptions],
                  ["Positive Indicators", a.detailedReport.positiveIndicators],
                  ["Recommendations", a.detailedReport.recommendations],
                ].map(([title, items]) =>
                  Array.isArray(items) && items.length ? (
                    <Box key={title} sx={{ mt: 1 }}>
                      <Typography sx={{ fontWeight: 800 }}>{title}</Typography>
                      <Box component="ul" sx={{ mt: 0.5 }}>
                        {items.map((x, i) => <li key={i}>{x}</li>)}
                      </Box>
                    </Box>
                  ) : null
                )}
              </Paper>
            )}

            {!!deck?.slides?.length && (
              <Box>
                <Typography sx={{ fontWeight: 900, mb: 1, color: "rgba(25,118,210,0.95)" }}>
                  Presentation • {deck.deckTitle}
                </Typography>
                <Stack spacing={1.5}>
                  {deck.slides.map((s, idx) => (
                    <Paper
                      key={idx}
                      elevation={1}
                      sx={{
                        borderRadius: 2,
                        p: 2.5,
                        minHeight: 210,
                        borderTop: `6px solid ${THEME_ORANGE}`,
                        backgroundColor: "#fff",
                      }}
                    >
                      <Typography sx={{ fontSize: "0.75rem", opacity: 0.6 }}>
                        Slide {s.slideNumber || idx + 1} • {s.type}
                      </Typography>
                      <Typography sx={{ fontSize: "1.25rem", fontWeight: 900, color: "rgba(25,118,210,0.95)", mb: 1 }}>
                        {s.title}
                      </Typography>
                      <Box component="ul">
                        {(s.bullets || []).map((b, i) => <li key={i}>{b}</li>)}
                      </Box>
                      {!!s.chartId && (
                        <Chip size="small" label={`Chart: ${s.chartId}`} sx={{ mt: 1 }} />
                      )}
                    </Paper>
                  ))}
                </Stack>
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={downloadAiReport} variant="outlined" sx={outlinedBtnSx}>
            Download Report
          </Button>
          <Button
            onClick={downloadPresentationHtml}
            variant="outlined"
            sx={outlinedBtnSx}
            disabled={!deck?.slides?.length}
          >
            Download Presentation
          </Button>
          <Button onClick={() => setAiOpen(false)} variant="contained" sx={primaryBtnSx}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  // ================== SAVE VIEW (UI ONLY) ==================

  const viewsKey = useMemo(() => `reportbuilder_views_${loginCampusCode}`, [loginCampusCode]);

  const [savedViews, setSavedViews] = useState([]);
  const [selectedViewName, setSelectedViewName] = useState("");

  const fetchViews = async () => {
    if (!loginCampusCode) return;
    const resp = await axios.get(`${API_URL}/api/reportbuilder/views`, {
      ...authHeader,
      params: { CampusCode: loginCampusCode, modelCode }, // you can omit modelCode if you want all
    });
    setSavedViews(Array.isArray(resp.data) ? resp.data : []);
  };

  useEffect(() => {
    fetchViews().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginCampusCode, modelCode]);

  const saveViewBackend = async () => {
  const name = String(viewName || "").trim();
  if (!name) {
    setToast({ open: true, type: "error", msg: "Please enter a view name." });
    return;
  }

  setSavingView(true);
    try {
      await axios.post(
        `${API_URL}/api/reportbuilder/views/save`,
        {
          CampusCode: loginCampusCode,
          viewName: name,
          modelCode,
          rows,
          cols,
          values,
          filters: (filters || []).map(({ FilterId, ...rest }) => rest),
        },
        { ...authHeader, params: { CampusCode: loginCampusCode } }
      );
  
      setSaveOpen(false);
      setToast({ open: true, type: "success", msg: "View saved!" });
      setSelectedViewName(name);
      await fetchViews();
    } catch (e) {
      setToast({ open: true, type: "error", msg: e?.response?.data?.error || "Save failed." });
    } finally {
      setSavingView(false);
    }
  };
  
  const applyView = (view) => {
    if (!view) return;
    setModelCode(view.modelCode || modelCode);
    setRows(Array.isArray(view.rows) ? view.rows : []);
    setCols(Array.isArray(view.cols) ? view.cols : []);
    setValues(Array.isArray(view.values) ? view.values : []);
    // restore filters with stable ids
    const restored = (view.filters || []).map((f) => ({
      FilterId: `F_${Date.now()}_${Math.random().toString(16).slice(2)}`,
      field: f.field || "",
      op: f.op || "=",
      value: f.value ?? "",
    }));
    setFilters(restored.length ? restored : [makeFilter()]);
  };

  const loadSelectedView = () => {
    const v = savedViews.find((x) => x?.ViewName === selectedViewName);
    if (!v) {
      setToast({ open: true, type: "error", msg: "View not found." });
      return;
    }
  
    // Apply
    setModelCode(v.ModelCode || modelCode);
    setRows(Array.isArray(v.rows) ? v.rows : []);
    setCols(Array.isArray(v.cols) ? v.cols : []);
    setValues(Array.isArray(v.values) ? v.values : []);
    const restored = (v.filters || []).map((f) => ({
      FilterId: `F_${Date.now()}_${Math.random().toString(16).slice(2)}`,
      field: f.field || "",
      op: f.op || "=",
      value: f.value ?? "",
    }));
    setFilters(restored.length ? restored : [makeFilter()]);
  
    setToast({ open: true, type: "success", msg: `Loaded view: ${v.ViewName}` });
  };

  {/*
    const deleteSelectedView = () => {
      if (!selectedViewName) return;
      const updated = savedViews.filter((v) => v?.name !== selectedViewName);
      localStorage.setItem(viewsKey, JSON.stringify(updated));
      setSavedViews(updated);
      setSelectedViewName("");
      setToast({ open: true, type: "success", msg: "View deleted." });
    };
    */}
    const deleteSelectedView = async () => {
      if (!selectedViewName) return;
      const yes = window.confirm(`Delete saved view "${selectedViewName}"?`);
      if (!yes) return;
    
      try {
        await axios.delete(`${API_URL}/api/reportbuilder/views/${encodeURIComponent(selectedViewName)}`, {
          ...authHeader,
          params: { CampusCode: loginCampusCode },
        });
    
        setToast({ open: true, type: "success", msg: "View deleted." });
        setSelectedViewName("");
        await fetchViews();
      } catch (e) {
        setToast({ open: true, type: "error", msg: e?.response?.data?.error || "Delete failed." });
      }
    };

  const canSaveView = useMemo(() => {
    return !!token && !!loginCampusCode && !!modelCode && (rows.length + cols.length + values.length) > 0;
  }, [token, loginCampusCode, modelCode, rows, cols, values]);

  const openSaveDialog = () => {
    setViewName(`${modelCode}_View_${new Date().toISOString().slice(0, 10)}`);
    setSaveOpen(true);
  };

  const saveViewLocal = async () => {
    const name = String(viewName || "").trim();
    if (!name) {
      setToast({ open: true, type: "error", msg: "Please enter a view name." });
      return;
    }

    try {
      setSavingView(true);
      const key = `reportbuilder_views_${loginCampusCode}`;
      const existing = JSON.parse(localStorage.getItem(key) || "[]");

      const view = {
        name,
        campusCode: loginCampusCode,
        modelCode,
        rows,
        cols,
        values,
        filters: (filters || []).map(({ FilterId, ...rest }) => rest),
        createdAt: new Date().toISOString(),
      };

      const updated = [view, ...existing.filter((v) => v?.name !== name)].slice(0, 50);
      localStorage.setItem(key, JSON.stringify(updated));
      
      //loadViewsFromStorage();
      setSelectedViewName(name);

      setSaveOpen(false);
      setToast({ open: true, type: "success", msg: "View saved (local)!" });
    } catch (e) {
      setToast({ open: true, type: "error", msg: e?.message || "Save failed." });
    } finally {
      setSavingView(false);
    }
  };

  // ================== UI PIECES ==================
  // ✅ Available fields: collapsible folders (fix #1)
  const FieldTree = () => {
    const folderNames = Object.keys(folders || {}).sort();

    const isFolderExpanded = (folderName) => {
      if (search?.trim()) return true; // auto-expand when searching
      return expandedFolders.has(folderName);
    };

    const toggleFolder = (folderName) => {
      setExpandedFolders((prev) => {
        const next = new Set(prev);
        if (next.has(folderName)) next.delete(folderName);
        else next.add(folderName);
        return next;
      });
    };

    return (
      <Box>
        {/* Add target selector (ROWS/COLS/VALUES/FILTERS) */}
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1 }}>
          <Typography sx={{ fontSize: "0.85rem", fontWeight: 900, color: "rgba(25,118,210,0.95)" }}>
            Add to
          </Typography>
          <FormControl size="small" sx={{ minWidth: 160 }} >
            <Select
              value={addTarget}
              onChange={(e) => setAddTarget(e.target.value)}
              sx={{
                borderRadius: 2,
                fontWeight: 900,
                backgroundColor: "rgba(25,118,210,0.03)",
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(25,118,210,0.22)" },
                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(25,118,210,0.45)" },
              }}
            >
              <MenuItem value="ROWS">Rows</MenuItem>
              <MenuItem value="COLS">Columns</MenuItem>
              <MenuItem value="VALUES">Values</MenuItem>
              <MenuItem value="FILTERS">Filters</MenuItem>
            </Select>
          </FormControl>

          <Tooltip title="Tip: choose Columns here, then click field chips to add to Columns.">
            <Typography sx={{ fontSize: "0.78rem", opacity: 0.75 }}>
              (then click fields)
            </Typography>
          </Tooltip>
        </Box>

        {folderNames.map((folderName) => {
          const raw = folders?.[folderName] || [];
          const items = raw.filter(
            (f) =>
              matchesSearch(folderName) ||
              matchesSearch(f.FieldLabel) ||
              matchesSearch(f.FieldCode)
          );

          if (!items.length) return null;

          return (
            <Accordion
              key={folderName}
              expanded={isFolderExpanded(folderName)}
              onChange={() => toggleFolder(folderName)}
              disableGutters
              elevation={0}
              sx={{
                border: "1px solid rgba(25,118,210,0.12)",
                borderRadius: 2,
                mb: 1,
                overflow: "hidden",
                "&:before": { display: "none" },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  backgroundColor: "rgba(25,118,210,0.04)",
                  "& .MuiAccordionSummary-content": { alignItems: "center", gap: 1 },
                }}
              >
                <Typography sx={{ fontWeight: 900, color: "rgba(25,118,210,0.95)" }}>
                  {folderName}
                </Typography>
                <Chip
                  size="small"
                  label={items.length}
                  sx={{
                    borderRadius: 2,
                    fontWeight: 900,
                    backgroundColor: "rgba(25,118,210,0.08)",
                    border: "1px solid rgba(25,118,210,0.15)",
                  }}
                />
              </AccordionSummary>

              <AccordionDetails sx={{ backgroundColor: "#fff" }}>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {items.map((f) => (
                    <Button
                      key={f.FieldCode}
                      variant="outlined"
                      size="small"
                      sx={{
                        ...subtleBtnSx,
                        fontWeight: 900,
                        px: 1.25,
                        py: 0.5,
                      }}
                      startIcon={<AddIcon />}
                      onClick={() => handleAddField(f.FieldCode)}
                      title={f.FieldCode}
                    >
                      {f.FieldLabel}
                    </Button>
                  ))}
                </Box>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>
    );
  };

  const ChipZone = ({ title, subtitle, items, onRemove, emptyHint }) => (
    <Paper elevation={0} sx={{ ...surfaceSx, p: 1.5 }}>
      <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
        <Box>
          <Typography sx={{ fontWeight: 900, color: "rgba(25,118,210,0.95)" }}>{title}</Typography>
          {!!subtitle && <Typography sx={{ fontSize: "0.82rem", opacity: 0.8 }}>{subtitle}</Typography>}
        </Box>
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
        {(items || []).length === 0 ? (
          <Typography sx={{ fontSize: "0.85rem", opacity: 0.7 }}>{emptyHint}</Typography>
        ) : (
          items.map((it) => (
            <Chip
              key={it}
              label={safeLabel(it)}
              onDelete={() => onRemove(it)}
              sx={{
                borderRadius: 2,
                fontWeight: 800,
                border: "1px solid rgba(25,118,210,0.18)",
                backgroundColor: "rgba(25,118,210,0.04)",
                "& .MuiChip-deleteIcon": { color: "rgba(25,118,210,0.75)" },
                "&:hover .MuiChip-deleteIcon": { color: THEME_ORANGE },
              }}
            />
          ))
        )}
      </Box>
    </Paper>
  );

  const ValueZone = () => (
    <Paper elevation={0} sx={{ ...surfaceSx, p: 1.5 }}>
      <Typography sx={{ fontWeight: 900, color: "rgba(25,118,210,0.95)" }}>Values</Typography>
      <Typography sx={{ fontSize: "0.82rem", opacity: 0.8 }}>
        Add “MEASURE / DISPLAY” fields here (e.g., Grade, GPA, CGPA)
      </Typography>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
        {values.length === 0 ? (
          <Typography sx={{ fontSize: "0.85rem", opacity: 0.7 }}>No values selected</Typography>
        ) : (
          values.map((v) => (
            <Chip
              key={v.field}
              label={safeLabel(v.field)}
              onDelete={() => setValues((arr) => removeValue(arr, v.field))}
              sx={{
                borderRadius: 2,
                fontWeight: 800,
                border: "1px solid rgba(25,118,210,0.18)",
                backgroundColor: "rgba(25,118,210,0.04)",
                "& .MuiChip-deleteIcon": { color: "rgba(25,118,210,0.75)" },
                "&:hover .MuiChip-deleteIcon": { color: THEME_ORANGE },
              }}
            />
          ))
        )}
      </Box>
    </Paper>
  );

  // Filters use stable FilterId keys; do not programmatically steal focus from other inputs.
  const FilterZone = () => (
    <Paper elevation={0} sx={{ ...surfaceSx, p: 1.5 }}>
      <Typography sx={{ fontWeight: 900, color: "rgba(25,118,210,0.95)" }}>Filters</Typography>
      <Typography sx={{ fontSize: "0.82rem", opacity: 0.8 }}>
        Tip: for Student Portal, keep StudentID filter. For Admin, add Branch/AcademicYear/etc.
      </Typography>
  
      <Stack spacing={1.25} sx={{ mt: 1 }}>
        {filters.map((f) => (
          <Box
            key={f.FilterId} // ✅ stable key
            sx={{
              display: "grid",
              gridTemplateColumns: "1.2fr 0.7fr 1.4fr auto",
              gap: 1,
              alignItems: "center",
            }}
          >
            <FormControl size="small" fullWidth sx={inputSx}>
              <InputLabel>Field</InputLabel>
              <Select
                label="Field"
                value={f.field || ""}
                onChange={(e) => {
                  const val = e.target.value;
                  setFilters((arr) =>
                    arr.map((x) => (x.FilterId === f.FilterId ? { ...x, field: val } : x))
                  );
                }}
              >
                {allFieldsFlat.map((ff) => (
                  <MenuItem key={ff.FieldCode} value={ff.FieldCode}>
                    {ff.Folder} • {ff.FieldLabel}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
              
            <FormControl size="small" fullWidth sx={inputSx}>
              <InputLabel>Op</InputLabel>
              <Select
                label="Op"
                value={f.op || "="}
                onChange={(e) => {
                  const val = e.target.value;
                  setFilters((arr) =>
                    arr.map((x) => (x.FilterId === f.FilterId ? { ...x, op: val } : x))
                  );
                }}
              >
                <MenuItem value="=">=</MenuItem>
                <MenuItem value="!=">!=</MenuItem>
                <MenuItem value="LIKE">LIKE</MenuItem>
                <MenuItem value="IN">IN</MenuItem>
                <MenuItem value=">">{">"}</MenuItem>
                <MenuItem value="<">{"<"}</MenuItem>
                <MenuItem value=">=">{">="}</MenuItem>
                <MenuItem value="<=">{"<="}</MenuItem>
              </Select>
            </FormControl>
              
            <TextField
              size="small"
              placeholder={String(f.op || "=").toUpperCase() === "IN" ? "Value1, Value2, Value3" : "Value"}
              helperText={String(f.op || "=").toUpperCase() === "IN" ? "Enter comma-separated values" : ""}
              value={f.value ?? ""}
              onChange={(e) => {
                const val = e.target.value;
                setFilters((arr) =>
                  arr.map((x) => (x.FilterId === f.FilterId ? { ...x, value: val } : x))
                );
              }}
              fullWidth
              sx={inputSx}
              inputProps={{ autoComplete: "off" }}
            />
  
            <IconButton
              sx={iconBtnSx}
              title="Remove filter"
              onClick={() => {
                setFilters((arr) => arr.filter((x) => x.FilterId !== f.FilterId));
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        ))}
  
        <Box>
          <Button
            variant="outlined"
            sx={outlinedBtnSx}
            startIcon={<AddIcon />}
            onClick={() => setFilters((arr) => [...arr, makeFilter()])}
          >
            Add Filter
          </Button>
        </Box>
      </Stack>
    </Paper>
  );

  const FlatPreview = () => {
  const colsOut = data?.length ? Object.keys(data[0]) : [];
  const colCount = colsOut.length;

  return (
      <Paper elevation={0} sx={{ ...surfaceSx, mt: 1, overflow: "visible" }}>
        <Box sx={hScrollSx}>
          <Box sx={{ minWidth: "max-content" }}>
            {/* ✅ Rounded wrapper */}
            <Box sx={previewTableWrapSx}>
              <TableContainer component="div" sx={{ overflow: "visible" }}>
                <Table size="small" sx={previewTableSx(colCount)}>
                  <TableHead>
                    <TableRow sx={theadRowSx}>
                      {colsOut.map((c) => (
                        <TableCell key={c} sx={{ whiteSpace: "nowrap", minWidth: 140 }}>
                          {c}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
  
                  <TableBody>
                    {(data || []).slice(0, 200).map((r, idx) => (
                      <TableRow key={idx} sx={zebraRowSx(idx)}>
                        {colsOut.map((c) => (
                          <TableCell key={c} sx={{ whiteSpace: "nowrap", minWidth: 140 }}>
                            {String(r?.[c] ?? "")}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Box>
        </Box>
      </Paper>
    );
  };
  
  const PivotPreview = () => {
  const hasPivotFields =
    (rows || []).length > 0 || (cols || []).length > 0 || (values || []).length > 0;

  if (!hasPivotFields) {
    return (
      <Paper elevation={0} sx={{ ...surfaceSx, mt: 1, p: 2 }}>
        <Typography sx={{ opacity: 0.8 }}>
          Add at least one field to Rows, Columns, or Values to show Pivot.
        </Typography>
      </Paper>
    );
  }

  const p = pivotData(data || [], rows, cols, values);
  const colsOut = p.cols || [];
  const colCount = colsOut.length;

  return (
    <Paper elevation={0} sx={{ ...surfaceSx, mt: 1, overflow: "visible" }}>
      <Box sx={hScrollSx}>
        <Box sx={{ minWidth: "max-content" }}>

          {/* ✅ Rounded wrapper */}
          <Box
            sx={{
              borderRadius: 2,
              overflow: "hidden",
              border: "1px solid rgba(25,118,210,0.15)",
              backgroundColor: "#fff",
            }}
          >

            <TableContainer component="div" sx={{ overflow: "visible" }}>
              <Table
                size="small"
                sx={{
                  width: "max-content",
                  minWidth: "100%",
                  tableLayout: "auto",
                  ...(colCount ? { minWidth: Math.max(900, colCount * 160) } : {}),
                  "& thead th:first-of-type": { borderTopLeftRadius: 12 },
                  "& thead th:last-of-type": { borderTopRightRadius: 12 },
                }}
              >
                <TableHead>
                  <TableRow sx={theadRowSx}>
                    {colsOut.map((c) => (
                      <TableCell key={c} sx={{ whiteSpace: "nowrap", minWidth: 140 }}>
                        {c}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {p.rows.slice(0, 100).map((r, idx) => (
                    <TableRow key={idx} sx={zebraRowSx(idx)}>
                      {colsOut.map((c) => (
                        <TableCell key={c} sx={{ whiteSpace: "nowrap", minWidth: 140 }}>
                          {String(r?.[c] ?? "")}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>

              </Table>
            </TableContainer>

          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

  return (
    <Box sx={pageWrapSx}>
      {/* ✅ Toast */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
      >
        <Alert severity={toast.type} sx={{ borderRadius: 2 }}>
          {toast.msg}
        </Alert>
      </Snackbar>

      {AiAnalyticsPanel()}

      {/* ✅ Save View dialog */}
      <Dialog open={saveOpen} onClose={() => setSaveOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>Save View</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "0.85rem", opacity: 0.8, mb: 1 }}>
            This saves locally for now (localStorage). Later we can wire to backend.
          </Typography>
          <TextField
            fullWidth
            label="View name"
            value={viewName}
            onChange={(e) => setViewName(e.target.value)}
            sx={inputSx}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button variant="outlined" sx={outlinedBtnSx} onClick={() => setSaveOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={primaryBtnSx}
            onClick={saveViewBackend}
            disabled={savingView || !String(viewName || "").trim()}
          >
            {savingView ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Paper elevation={5} sx={rootPaperSx}>
        <Box sx={headerSx}>
          <Box>
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 900, lineHeight: 1.2 }}>
              Self-Service Report Builder
            </Typography>
            <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
              Choose fields → design pivot → preview
              {loginCampusCode ? ` • Campus: ${loginCampusCode}` : ""}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>

            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel sx={{ color: "white" }}>Saved Views</InputLabel>
              <Select
                label="Saved Views"
                value={selectedViewName}
                onChange={(e) => setSelectedViewName(e.target.value)}
                sx={{
                  color: "white",
                  fontWeight: 900,
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.70)" },
                  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "white" },
                  "& .MuiSvgIcon-root": { color: "white" },
                }}
              >
                {(savedViews || []).map((v) => (
                  <MenuItem key={v.name} value={v.name}>
                    {v.name}
                  </MenuItem>
                ))}
                {/*
                {(!savedViews || savedViews.length === 0) && (
                  <MenuItem value="" disabled>
                    No saved views
                  </MenuItem>
                )}
                */}
                {(savedViews || []).map((v) => (
                  <MenuItem key={v.ViewId || v.ViewName} value={v.ViewName}>
                    {v.ViewName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <Button
              variant="outlined"
              sx={outlinedBtnSx}
              disabled={!selectedViewName}
              onClick={loadSelectedView}
            >
              Load
            </Button>
            
            <Button
              variant="outlined"
              sx={outlinedBtnSx}
              disabled={!selectedViewName}
              onClick={deleteSelectedView}
            >
              Delete
            </Button>

            <Button
              variant="outlined"
              sx={outlinedBtnSx}
              startIcon={<RefreshIcon />}
              onClick={() => {
                setError("");
                if (!loginCampusCode) return;
                fetchModels().catch((e) =>
                  setError(e?.response?.data?.error || e.message || "Failed to load models")
                );
                fetchFields(modelCode).catch((e) =>
                  setError(e?.response?.data?.error || e.message || "Failed to load fields")
                );
              }}
            >
              Refresh
            </Button>

            <Button
              variant="contained"
              sx={primaryBtnSx}
              onClick={runReport}
              disabled={loading || !token || !loginCampusCode}
            >
              {loading ? "Running..." : "Run"}
            </Button>

            {/* ✅ Save view enabled (fix #4) */}
            <Button
              variant="outlined"
              sx={outlinedBtnSx}
              disabled={!canSaveView}
              onClick={openSaveDialog}
            >
              Save View
            </Button>

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <Select
                value={aiOutputType}
                onChange={(e) => setAiOutputType(e.target.value)}
                sx={{
                  color: "white",
                  fontWeight: 900,
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.70)" },
                  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "white" },
                  "& .MuiSvgIcon-root": { color: "white" },
                }}
              >
                <MenuItem value="ALL">AI Full Analysis</MenuItem>
                <MenuItem value="INSIGHTS">AI Insights</MenuItem>
                <MenuItem value="CHARTS">AI Charts</MenuItem>
                <MenuItem value="REPORT">AI Report</MenuItem>
                <MenuItem value="PRESENTATION">AI Presentation</MenuItem>
              </Select>
            </FormControl>

            <Button
              variant="contained"
              sx={{
                ...primaryBtnSx,
                backgroundColor: "rgba(25,118,210,0.95)",
                "&:hover": { backgroundColor: "rgba(25,118,210,0.82)" },
              }}
              onClick={() => generateAiAnalytics(aiOutputType)}
              disabled={aiLoading || !token || !loginCampusCode || (rows.length + cols.length + values.length) === 0}
            >
              {aiLoading ? "Generating AI..." : "Generate AI"}
            </Button>

            <Button
              variant="outlined"
              sx={outlinedBtnSx}
              startIcon={<DownloadIcon />}
              onClick={() => (tab === 0 ? downloadFlat() : downloadPivot())}
              disabled={!data?.length}
            >
              Download Excel
            </Button>

          </Box>
        </Box>

        <Box sx={bodySx}>
          {aiOutputType === "CHARTS" && (
            <Paper elevation={0} sx={{ ...surfaceSx, p: 2, mb: 2, borderLeft: `5px solid ${THEME_ORANGE}` }}>
              <Typography sx={{ fontWeight: 900, color: "rgba(25,118,210,0.95)" }}>
                What do you want the charts to determine?
              </Typography>
              <Typography sx={{ fontSize: "0.82rem", opacity: 0.72, mb: 1.2 }}>
                Describe the comparison, trend, problem, or decision you are investigating. AI will choose meaningful chart types and UniFLOO will calculate the chart values from the report data.
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={2}
                maxRows={5}
                value={aiChartPrompt}
                onChange={(e) => setAiChartPrompt(e.target.value)}
                placeholder="Example: Which subjects have the highest failure counts? Compare pass/fail by branch and show whether GPA improves across semesters."
                sx={inputSx}
              />
              <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                {[
                  "Show the weakest subjects and where failures are concentrated",
                  "Compare performance across branches and semesters",
                  "Show GPA/marks trends and identify unusual changes",
                  "Find meaningful relationships between numeric performance measures",
                ].map((example) => (
                  <Chip key={example} size="small" clickable label={example} onClick={() => setAiChartPrompt(example)} />
                ))}
              </Stack>
            </Paper>
          )}
          {!!error && (
            <Alert
              severity="error"
              onClose={() => setError("")}
              sx={{
                borderRadius: 2,
                border: "1px solid rgba(244,67,54,0.25)",
                backgroundColor: "rgba(244,67,54,0.04)",
                mb: 2,
              }}
            >
              {error}
            </Alert>
          )}

          {!!success && (
            <Snackbar open autoHideDuration={2500} onClose={() => setSuccess(false)}>
              <Alert severity="success" sx={{ borderRadius: 2 }}>
                Report loaded!
              </Alert>
            </Snackbar>
          )}

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "0.70fr 1.05fr 1.90fr" },
              gap: 2,
            }}
          >
            {/* LEFT */}
            <Paper elevation={0} sx={{ ...surfaceSx, p: 2 }}>
              <Typography sx={{ fontWeight: 900, color: "rgba(25,118,210,0.95)", mb: 1 }}>
                Available Fields
              </Typography>

              <TextField
                size="small"
                fullWidth
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search fields..."
                sx={inputSx}
              />

              <Divider sx={{ my: 1.5, borderColor: "rgba(25,118,210,0.10)" }} />

              <Box sx={{ mb: 1.5 }}>
                <FormControl size="small" fullWidth sx={inputSx}>
                  <InputLabel>Report Model</InputLabel>
                  <Select
                    label="Report Model"
                    value={modelCode}
                    onChange={(e) => setModelCode(e.target.value)}
                  >
                    {(models || []).map((m) => (
                      <MenuItem
                        key={m.ModelCode || m.modelCode}
                        value={m.ModelCode || m.modelCode}
                      >
                        {m.ModelName || m.modelName || (m.ModelCode || m.modelCode)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <FieldTree />
            </Paper>

            {/* MIDDLE */}
            <Paper elevation={0} sx={{ ...surfaceSx, p: 2 }}>
              <Typography sx={{ fontWeight: 900, color: "rgba(25,118,210,0.95)", mb: 1 }}>
                Designer
              </Typography>

              <Stack spacing={1.5}>
                <ChipZone
                  title="Rows"
                  subtitle="Dimensions on the left side of pivot"
                  items={rows}
                  onRemove={(fc) => setRows((arr) => removeOne(arr, fc))}
                  emptyHint="Add fields (DIM) to Rows (e.g., Semester, StudentID)"
                />
                <ChipZone
                  title="Columns"
                  subtitle="Optional — dimensions across pivot columns"
                  items={cols}
                  onRemove={(fc) => setCols((arr) => removeOne(arr, fc))}
                  emptyHint="Optional. Leave blank for a row-only Excel-style pivot."
                />
                <ValueZone />
                {FilterZone()}
              </Stack>
            </Paper>

            {/* RIGHT */}
            {/*<Paper elevation={0} sx={{ ...surfaceSx, p: 2 }}>*/}
            <Paper elevation={0} sx={previewCardSx}>  
              <Typography sx={{ fontWeight: 900, color: "rgba(25,118,210,0.95)", mb: 1 }}>
                Preview
              </Typography>
              <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 1 }}>
                <Tab label="Flat" />
                <Tab label="Pivot" />
              </Tabs>
              {tab === 0 ? <FlatPreview /> : <PivotPreview />}
            </Paper>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
} 